import { forwardRef } from 'react';
import { Link } from '@/components/link';
import { ProductTileCartOrQuickAdd } from './product-tile-cart-or-quick-add';
import { ProductTileImageCustom } from './product-tile-image-custom';
import { ProductTileTagOverflowRow } from './product-tile-tag-overflow-row';
import { productCardFocusRingClass } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { WishlistIconButton } from '../buttons/wishlist-icon-button';
import { PRODUCT_TILE_CUSTOM_CARD_SHELL_CLASS, type ProductTileCustomCardProps } from './product-tile-custom-types';
import { Trans } from 'react-i18next';
import { SvgInfo } from '../icons-eataly';
import { getDisplayQuantity } from '@/lib/sold-by-weight';

function ProductTileCustomHorizontalCardInner(
    {
        product,
        productName,
        brand,
        productUrl,
        effectiveImgAspectRatio,
        handleProductClick,
        productHighlight,
        eatingList,
        priceAndWeight,
        tileBasketLine,
        basketControlsDisabled,
        t,
        listFormatted,
        isOnSale,
        className,
        ...rest
    }: ProductTileCustomCardProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const previousPriceTransComponents = {
        priceGroup: <span className="inline-flex items-center gap-1" />,
        price: <span className="body-copy-s-bold line-through text-neutral-10" />,
        icon: <SvgInfo className="size-4 shrink-0 text-neutral-10" aria-hidden />,
    };
    const hasBasketQuantity = getDisplayQuantity(tileBasketLine, 0) > 0;
    return (
        <div
            ref={ref}
            data-testid="product-tile"
            className={cn(
                'relative w-full min-w-0 max-w-full cursor-default gap-0 border-0 py-0',
                'min-h-44 flex flex-row items-stretch gap-2 overflow-hidden',
                PRODUCT_TILE_CUSTOM_CARD_SHELL_CLASS,
                className
            )}
            {...rest}>
            {/** First in DOM so Tab hits the PDP link, then wishlist and cart controls. */}
            <Link
                to={productUrl}
                className="peer absolute inset-0 z-10 rounded-3xl focus-visible:outline-none"
                aria-label={t('viewProductAriaLabel', { productName }) ?? `View ${productName}`}
                onClick={() => handleProductClick?.(product)}
            />
            <div aria-hidden className={productCardFocusRingClass} />
            <div className="relative h-full min-h-32 w-28 min-w-0 max-h-44 flex-shrink-0 self-stretch overflow-hidden rounded-lg bg-transparent">
                <ProductTileImageCustom
                    variant="plp-horizontal"
                    product={product}
                    pdpLink={false}
                    imgAspectRatio={effectiveImgAspectRatio}
                    handleProductClick={handleProductClick}
                />
            </div>

            {/** Fills image height; `mt-auto` on the price block pins it to the bottom when the tag row is empty. */}
            <div
                className={cn(
                    'relative flex min-h-0 min-w-0 flex-1 flex-col gap-2 self-stretch',
                    hasBasketQuantity && 'gap-0'
                )}>
                <div className="flex w-full min-w-0 flex-row gap-2">
                    <div className="flex w-full min-w-0 flex-col gap-1">
                        <p className="w-full min-w-0 body-copy-s text-neutral-40 whitespace-nowrap line-clamp-1">
                            {brand || '\u00A0'}
                        </p>
                        <p className=" w-full min-w-0 line-clamp-2 text-left body-copy-m-bold">
                            <span className="text-left">{productName}</span>
                        </p>
                    </div>
                    <div className="pointer-events-auto relative z-20 flex w-11 shrink-0 h-10 max-h-10 min-h-10 items-center justify-center rounded-[40px] p-0">
                        <WishlistIconButton
                            buttonVariant="small"
                            product={product}
                            className="size-6 text-[var(--neutral-20)] rounded-md"
                        />
                    </div>
                </div>

                {!hasBasketQuantity && (
                    <ProductTileTagOverflowRow
                        highlight={productHighlight}
                        eatingStyles={eatingList}
                        className="w-full shrink-0"
                    />
                )}
                {!hasBasketQuantity && isOnSale && listFormatted && (
                    <div className="flex w-full min-w-0 items-center justify-start gap-1">
                        <Trans
                            t={t}
                            i18nKey="price.previousPrice"
                            parent="span"
                            className="body-copy-s text-neutral-10 whitespace-pre-wrap"
                            values={{ price: listFormatted ?? '' }}
                            components={previousPriceTransComponents}
                        />
                    </div>
                )}

                <div className="flex w-full items-end justify-between mt-auto">
                    <div className="mt-auto w-full">{priceAndWeight}</div>
                    <div className="pointer-events-none relative z-20 w-11">
                        <ProductTileCartOrQuickAdd
                            product={product}
                            productName={productName}
                            display="icon"
                            quantityLayout="vertical"
                            basketLine={tileBasketLine}
                            disabled={basketControlsDisabled === true}
                            productUrl={productUrl}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

const ProductTileCustomHorizontalCard = forwardRef(ProductTileCustomHorizontalCardInner);

export { ProductTileCustomHorizontalCard };
